// Daily Quest Board (Oct 2026) — 3 daily quests, 7-day login streak and a
// weekly war chest, presented as parchment notices pinned to a war-table board.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import "./QuestBoard.css";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export default function QuestBoard() {
  const { user, authEnabled, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/quests/today`, { withCredentials: true });
      setData(d);
    } catch (e) { setData(null); }
  }, []);
  useEffect(() => {
    if (user && authEnabled) load();
  }, [user, authEnabled, load]);
  const claim = async (url, key, label) => {
    setBusy(key);
    try {
      const { data: r } = await axios.post(`${API}${url}`, {}, { withCredentials: true });
      setToast(`${label}: +${r.gold} gold${r.gems ? ` · +${r.gems} gems` : ""}${r.shards ? ` · +${r.shards} treasure` : ""}${r.cards_completed && r.cards_completed.length ? " · A new card is yours!" : ""}`);
      setTimeout(() => setToast(null), 3500);
      await load();
      if (typeof refresh === "function") { try { await refresh(); } catch (e) { /* ignore */ } }
    } catch (e) {
      setToast(e?.response?.data?.detail || "Could not claim");
      setTimeout(() => setToast(null), 3500);
    }
    setBusy(null);
  };
  if (!user || !authEnabled || !data) return null;
  const { quests, streak, chest } = data;
  const chestReady = chest.progress >= chest.target && !chest.claimed;
  return (
    <section className="qb-board" data-testid="quest-board">
      <div className="qb-header">
        <span className="qb-kicker">Orders of the Day</span>
        <h2 className="qb-title">The War Table</h2>
      </div>
      <div className="qb-notes">
        {quests.map((q, i) => {
          const done = q.progress >= q.target;
          return (
            <article key={q.id} className={`qb-note${q.claimed ? " claimed" : ""}${done && !q.claimed ? " ready" : ""}`} style={{ "--tilt": `${[-2, 1.5, -1][i % 3]}deg` }} data-testid={`quest-${q.id}`}>
              <span className="qb-pin" aria-hidden="true" />
              <h3 className="qb-note-title">{q.title}</h3>
              <p className="qb-note-desc">{q.desc}</p>
              <div className="qb-progress"><div className="qb-progress-fill" style={{ width: `${Math.min(100, (q.progress / q.target) * 100)}%` }} /></div>
              <div className="qb-note-foot">
                <span className="qb-count">{Math.min(q.progress, q.target)} / {q.target}</span>
                <span className="qb-reward">{q.gold} gold{q.gems ? ` · ${q.gems} gems` : ""}{q.shards ? ` · ${q.shards} treasure` : ""}</span>
              </div>
              {q.claimed ? (
                <div className="qb-stamp">Fulfilled</div>
              ) : (
                <button type="button" className="qb-btn" disabled={!done || busy === q.id} onClick={() => claim(`/quests/claim/${q.id}`, q.id, q.title)} data-testid={`quest-claim-${q.id}`}>
                  {done ? "Claim Spoils" : "In Progress"}
                </button>
              )}
            </article>
          );
        })}
      </div>
      <div className="qb-row">
        <div className="qb-streak" data-testid="quest-streak">
          <div className="qb-row-title">Daily Muster · Day {streak.day}</div>
          <div className="qb-seals">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <span key={d} className={`qb-seal${d < streak.day || (d === streak.day && !streak.can_claim) ? " lit" : ""}${d === Math.min(streak.day, 7) && streak.can_claim ? " today" : ""}`}>{d}</span>
            ))}
          </div>
          <button type="button" className="qb-btn qb-btn-gold" disabled={!streak.can_claim || busy === "streak"} onClick={() => claim("/quests/streak/claim", "streak", "Daily Muster")} data-testid="quest-streak-claim">
            {streak.can_claim ? `Answer the Muster · ${streak.gold} gold${streak.gems ? ` + ${streak.gems} gems` : ""} + ${streak.shards} treasure` : "Mustered Today"}
          </button>
        </div>
        <div className={`qb-chest${chestReady ? " ready" : ""}`} data-testid="quest-chest">
          <div className="qb-row-title">The War Chest</div>
          <div className="qb-chest-icon" aria-hidden="true" />
          <div className="qb-progress"><div className="qb-progress-fill" style={{ width: `${(chest.progress / chest.target) * 100}%` }} /></div>
          <div className="qb-count">{chest.progress} / {chest.target} orders fulfilled this week</div>
          <button type="button" className="qb-btn qb-btn-gold" disabled={!chestReady || busy === "chest"} onClick={() => claim("/quests/chest/claim", "chest", "War Chest")} data-testid="quest-chest-claim">
            {chest.claimed ? "Opened This Week" : chestReady ? `Open · ${chest.gold} gold + ${chest.gems} gems + ${chest.shards} treasure` : "Locked"}
          </button>
        </div>
      </div>
      {toast && <div className="qb-toast" role="status">{toast}</div>}
    </section>
  );
}
